import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaDroplet, FaChartPie } from "react-icons/fa6";

// Палітра кольорів, що відповідає світлячкам
const PALETTE_HEX = {
  amber: "#fbbf24",
  emerald: "#34d399",
  cyan: "#22d3ee",
  purple: "#c084fc",
  rose: "#fb7185",
};

export default function ActivitySphere({ allMoods = [] }) {
  const { t } = useTranslation();

  // 1. Аналізуємо дані: рахуємо частоту кожного кольору
  const { total, colorData } = useMemo(() => {
    if (!allMoods || !Array.isArray(allMoods) || allMoods.length === 0) {
      return { total: 0, colorData: [] };
    }

    const counts = {};
    let totalActs = 0;

    allMoods.forEach((moodEntry) => {
      if (moodEntry.activities && Array.isArray(moodEntry.activities)) {
        moodEntry.activities.forEach((act) => {
          const c = act.color || "emerald";
          counts[c] = (counts[c] || 0) + 1;
          totalActs++;
        });
      }
    });

    // Формуємо масив з відсотками для кожного кольору
    const data = Object.keys(counts).map((key) => ({
      color: key,
      count: counts[key],
      pct: Math.round((counts[key] / totalActs) * 100),
    }));

    // Сортуємо від найбільшого до найменшого
    data.sort((a, b) => b.count - a.count);

    return { total: totalActs, colorData: data };
  }, [allMoods]);

  const isEmpty = total === 0;

  // 2. Генеруємо "краплі" рідини для сфери на основі відсотків
  // Масив відсортований від найбільшого до найменшого.
  // Рендеримо у зворотньому порядку (від найменшого до найбільшого),
  // щоб найбільший blob був поверх усіх — але він і найбільший,
  // тому менші краплі все одно будуть видні з-під нього по краях.
  const blobs = useMemo(() => {
    const arr = [];
    colorData.forEach((cData, i) => {
      // Базовий розмір краплі залежить від її частки (від 40% до 120%)
      const baseSize = 40 + (cData.pct / 100) * 80;

      arr.push({
        id: `${cData.color}-1`,
        hex: PALETTE_HEX[cData.color],
        size: `${baseSize}%`,
        anim: `floatBlob1 ${10 + i * 2}s ease-in-out infinite alternate`,
        delay: `${i * 0.5}s`,
        // Індекс шару: найбільший (i=0) → найбільший zIndex → рендериться останнім
        zIndex: colorData.length - i,
      });
      arr.push({
        id: `${cData.color}-2`,
        hex: PALETTE_HEX[cData.color],
        size: `${baseSize * 0.8}%`,
        anim: `floatBlob2 ${12 + i * 2}s ease-in-out infinite alternate-reverse`,
        delay: `${i * 0.7}s`,
        zIndex: colorData.length - i,
      });
    });

    // Сортуємо: найбільший zIndex (найменші активності) — спочатку,
    // найменший zIndex (найбільша активність) — останнім.
    // Це означає що DOM порядок іде від малих до великих,
    // і великий blob буде поверх решти — але оскільки він найбільший,
    // менші краплі виглядатимуть з-під нього, а не навпаки.
    arr.sort((a, b) => b.zIndex - a.zIndex);
    const [first, ...rest] = arr;
    return first ? [first, ...rest.reverse()] : arr;
  }, [colorData]);

  return (
    // Стилі узгоджені зі StatisticsBlocks: bg-slate-800, border-slate-700, rounded-2xl
    <div className="w-full bg-slate-800 border border-slate-700 rounded-2xl p-5 shadow-lg mt-6">
      {/* КЛЮЧОВІ АНІМАЦІЇ ДЛЯ РІДИНИ */}
      <style>{`
        @keyframes floatBlob1 {
          0% { transform: translate(0%, 0%) rotate(0deg) scale(1); border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%; }
          50% { transform: translate(20%, 15%) rotate(90deg) scale(1.1); border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          100% { transform: translate(-10%, 20%) rotate(180deg) scale(1); border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%; }
        }
        @keyframes floatBlob2 {
          0% { transform: translate(10%, -10%) rotate(0deg) scale(1); border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          50% { transform: translate(-15%, 20%) rotate(-90deg) scale(1.2); border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
          100% { transform: translate(20%, -5%) rotate(-180deg) scale(0.9); border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
        }
      `}</style>

      {/* ШАПКА БЛОКУ */}
      <div className="flex items-center gap-3 mb-5 px-1">
        <div className="p-2 bg-slate-700/50 rounded-xl">
          <FaChartPie className="text-emerald-400 w-4 h-4" />
        </div>
        <div>
          <h3 className="text-white font-bold text-lg leading-none">
            {t("activitySphere.activitySphereTitle") || "Енергія активностей"}
          </h3>
          {/* <p className="text-xs text-slate-400 mt-1">
            {t("activitySphere.activitySphereDesc") || "Співвідношення подій за період"}
          </p> */}
        </div>
      </div>

      <div className="flex items-center gap-6">
        {/* --- СФЕРА З РІДИНОЮ --- */}
        <div
          className="relative w-32 h-32 flex-shrink-0 rounded-full overflow-hidden"
          style={{
            boxShadow: `
              inset -10px -10px 30px rgba(0,0,0,0.8),
              inset 10px 10px 20px rgba(255,255,255,0.1),
              0 10px 20px rgba(0,0,0,0.3)
            `,
            backgroundColor: "#0d1b24",
          }}
        >
          {/* Контейнер для рідини */}
          {!isEmpty && (
            <div className="absolute inset-0 filter blur-[12px] opacity-90">
              {blobs.map((blob) => (
                <div
                  key={blob.id}
                  className="absolute mix-blend-normal opacity-80"
                  style={{
                    backgroundColor: blob.hex,
                    width: blob.size,
                    height: blob.size,
                    left: "10%",
                    top: "10%",
                    animation: blob.anim,
                    animationDelay: blob.delay,
                    // zIndex не потрібен всередині blur-контейнера,
                    // DOM-порядок вже забезпечує правильне перекриття
                  }}
                />
              ))}
            </div>
          )}

          {/* ПОРОЖНІЙ СТАН */}
          {isEmpty && (
            <div className="absolute inset-0 flex flex-col items-center justify-center opacity-30">
              <FaDroplet className="text-slate-400 w-8 h-8 drop-shadow-md" />
            </div>
          )}

          {/* ВІДБЛИСК СКЛА (GLARE) */}
          <div
            className="absolute top-[5%] left-[15%] w-[70%] h-[35%] rounded-[50%] pointer-events-none"
            style={{
              background:
                "linear-gradient(to bottom, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)",
              transform: "rotate(-15deg)",
            }}
          />
          <div className="absolute inset-0 rounded-full border-[2px] border-white/5 pointer-events-none" />
        </div>

        {/* --- ЛЕГЕНДА (СТАТИСТИКА) --- */}
        <div className="flex flex-col flex-1 gap-3 overflow-hidden pr-2">
          {isEmpty ? (
            <p className="text-sm text-slate-500 italic">
              {t("activitySphere.noActivitiesYet") || "Тут поки порожньо..."}
            </p>
          ) : (
            colorData.map((item) => (
              <div key={item.color} className="flex items-center gap-3">
                {/* Кольорова точка */}
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0 shadow-sm"
                  style={{ backgroundColor: PALETTE_HEX[item.color] }}
                />

                {/* Прогрес-бар */}
                <div className="flex-1 h-2 bg-slate-900 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      backgroundColor: PALETTE_HEX[item.color],
                      width: `${item.pct}%`,
                      opacity: 0.8,
                    }}
                  />
                </div>

                {/* Відсотки */}
                <span className="text-sm font-bold text-slate-300 w-9 text-right">
                  {item.pct}%
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
